import { Fragment } from "react";
import type { LookView, StoryView, ThingView, ThreadView } from "../../shared/story";
import { roman } from "../../shared/plates";
import { Credit, TrustLinks } from "../trust";
import { WhereYouAre } from "../noirmap";
import { Care, Decision, VoiceShift, DeleteStory, Kept, SceneBreak, Weaving, waiting, More, SoundToggle, ChapterEnd, DreamNow, HurryHint, frayOf, inkOf, Plates, PlatesLink, Returned, SkinPicker, Unwoven, ReadingRoom, holdToHurry, Writer, linkify, quietFor, threadOrder, type ThemeProps } from "../common";
import { Margins } from "../v2/margins";
import { PenLine, Pull } from "../v2/pens";
import { LookActions } from "../v2/pocket";
import type { Shown, StoryUI } from "../useStory";
import "./edition.css";

/**
 * Night Edition: you write the headlines (owner's pick, 2026-10-18; mock-up
 * https://claude.ai/artifact/LA1ArVrSHh3ihtdBmRZWWG). Newsprint, a blackletter masthead, one red. Each line you send
 * is set as the headline and the narrator files the story under it, datelined where you were; plates print as
 * halftones; the open threads are "developing"; a chapter closes with the late final.
 */

/** A line this long is set as a headline; a longer one runs as a bold standfirst. */
const HEADLINE_MAX = 120;
const QUIET = 3;

/** Where the player was in turn n, for the dateline ("THE THIRD CARRIAGE —"). */
function datelineOf(view: StoryView | null, n: number): string | null {
  const here = view?.turns.find((t) => t.n === n)?.here;
  return here ? view!.things.find((t) => t.id === here)?.name ?? null : null;
}

function Headline({ t, meta }: { t: { input: string; kept?: string[] }; meta: boolean }) {
  if (meta) return <p className="ed-letter"><span className="ed-kicker">To the editor</span> {t.input}</p>;
  const long = t.input.length > HEADLINE_MAX;
  return (
    <header className="ed-headline-wrap">
      {long ? <p className="ed-standfirst"><span className="sr">You: </span><PenLine input={t.input} /></p> : <h2 className="ed-headline"><span className="sr">You: </span><PenLine input={t.input} /></h2>}
      <span className="ed-kicker">your line<Kept t={t} className="ed-kept" /></span>
    </header>
  );
}

function InDetail({ s, l, onLook }: { s: StoryUI; l: LookView; onLook(name: string): void }) {
  return (
    <aside className="ed-detail" aria-label={`In detail: ${l.thing}`}>
      <span className="ed-kicker">In detail · {l.thing}</span>
      <p>{l.description}</p>
      <p className="ed-detail-kids">
        <span className="ed-kicker">Closer:</span>{" "}
        {l.children.map((ch) => (
          <button key={ch.id} type="button" className="lm-thing" title={ch.hint} onClick={() => onLook(ch.name)}>{ch.name}</button>
        ))}
      </p>
      <LookActions s={s} thing={l.thing} className="ed-kicker" />
    </aside>
  );
}

function Story({ s, t, things, looks, onLook, live }: {
  s: StoryUI; t: Shown; things: readonly ThingView[]; looks?: LookView[]; onLook: ((name: string) => void) | null;
  live?: { streaming: boolean; paused: boolean };
}) {
  const meta = t.intent === "meta";
  const dateline = live ? null : datelineOf(s.view, t.n);
  return (
    <article className="ed-turn" data-turn={t.n}>
      <SceneBreak t={t} className="ed-break" mark="◆ ◆ ◆" />
      <Headline t={t} meta={meta} />
      <div className={`ed-article${meta ? " ed-meta" : ""}`} {...(live ? holdToHurry(s) : {})}>
        <Returned t={t} className="ed-return" />
        <VoiceShift t={t} className="ed-voice-shift" />
        <Decision t={t} className="ed-decision" />
        {dateline && !meta && <p className="ed-dateline">{dateline}</p>}
        <p data-prose={meta ? undefined : t.n} {...(live ? { "data-live": "" } : {})} className={meta ? "" : "ed-body"}>
          {meta && <span className="ed-kicker">Editor's note · </span>}
          {linkify(t.prose, things, meta ? null : onLook, "lm-thing", inkOf(s.view, t.n))}
          {t.status === "cut" && <span className="ed-spike" title="you cut in here" aria-label="cut here"> ✕</span>}
          <Unwoven s={s} t={t} label={(n) => `${n} ${n === 1 ? "word" : "words"} cut from the column`} />
          {live?.streaming && <span className="ed-caret" aria-hidden="true" />}
        </p>
        {live?.paused && <p className="ed-held">The presses hold while you type · enter cuts in here</p>}
        {live && <HurryHint s={s} className="ed-held" />}
        {looks?.map((l) => onLook && <InDetail key={l.thing} s={s} l={l} onLook={onLook} />)}
        {!meta && !live && <Pull s={s} n={t.n} className="ed-pull" />}
        {!meta && !live && <Margins s={s} n={t.n} className="ed-margins" />}
        <ChapterEnd s={s} t={t} className="ed-chapter" />
      </div>
    </article>
  );
}

/** Developing: the open threads; tap one to bring it back on your next line. */
function Developing({ s, threads, now, className = "" }: { s: StoryUI; threads: readonly ThreadView[]; now: number; className?: string }) {
  const shown = threadOrder(threads).filter((t) => t.state === "open").slice(0, 6);
  if (!shown.length) return null;
  return (
    <div className={`ed-developing ${className}`}>
      <span className="ed-kicker ed-red">Developing</span>
      <ul aria-label="Threads: tap one to bring it back on your next line">
        {shown.map((t) => {
          const quiet = quietFor(t, now) >= QUIET;
          return (
            <li key={t.id} title={t.hint}>
              {s.id
                ? <button type="button" className={`lm-pull${quiet ? " ed-cold" : ""}`} data-fray={frayOf(t, now)} onClick={() => s.pull(t)} disabled={s.busy} title={`Bring it back: ${t.hint}`}>{t.name}</button>
                : <span>{t.name}</span>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

export function EditionTheme({ s, home, skin, setSkin, goHome, openPlates, meta }: ThemeProps) {
  const view = s.view;
  const threads = view?.threads ?? [];
  const things = view?.things ?? [];
  const lookAt = (n: number) => (name: string) => s.look(n, name);

  const now = view?.n ?? s.live?.n ?? 0;
  const starting = !!home && !s.pending;
  const chapter = view?.chapter && !view.free ? `Chapter ${roman(view.chapter.n)}` : null;
  const placeholder = s.live?.streaming ? "typing holds the presses; enter cuts in" : s.id ? "What do you do?" : "Begin your story…";

  return (
    <main className="sk-edition">
      <div className="ed-frame">
        <div className="ed-main">
          <header className="ed-head">
            <div className="ed-mast">
              <a className="ed-brand" href="/" aria-label={s.id ? "Your stories" : undefined} onClick={(e) => { e.preventDefault(); if (s.id) meta("stories"); else goHome(); }}>The Weftwake{s.id && <span className="lm-brand-caret" aria-hidden="true"> ▾</span>}</a>
              <SoundToggle className="ed-sound" />
              <SkinPicker skin={skin} setSkin={setSkin} className="ed-skins" />
            </div>
            <p className="ed-folio">
              <span>Night edition</span>
              <span className="ed-folio-title">{view ? view.title : s.live ? "" : "No. 1"}</span>
              <span>{chapter ? `${chapter} · ` : ""}{now ? `turn ${now}` : ""}</span>
            </p>
            {view && <Developing s={s} threads={threads} now={now} className="ed-phone-only" />}
          </header>

          {starting && (
            <section className="ed-home">
              <h1 className="ed-home-h">Tonight's story hasn't been written</h1>
              <p className="ed-lede">You write the headline: what you do, where you are, what's true. The narrator files the story under it.</p>
              <p className="lm-what">Weftwake is a text adventure that writes itself as you play: you write what you do, an AI narrator writes what happens. Your first 40 turns are free.</p>
              <div className="ed-seeds" aria-label="Or begin with">
                {home.seeds.map((o) => (
                  <button key={o} type="button" onClick={() => s.send(o)} disabled={s.busy}>{o}</button>
                ))}
              </div>
            </section>
          )}

          <div className="ed-columns" role="log" aria-live="polite" aria-label="The story" aria-busy={!!s.live?.streaming || !!s.pending}>
            {view?.recap && s.turns.length > 0 && s.turns[0]!.n > 1 && (
              <p className="ed-recap ed-phone-only"><span className="ed-kicker">Previously</span> {view.recap}</p>
            )}
            {s.loadFailed && (
              <p className="ed-notice">Couldn't open this story. <button type="button" onClick={s.reload}>Try again</button></p>
            )}
            {s.turns.map((t) => (
              <Fragment key={t.n}>
                <Story s={s} t={t} things={things} looks={s.looks[t.n]} onLook={s.id ? lookAt(t.n) : null} />
                <Plates s={s} at={t.n} className="ed-plate" />
              </Fragment>
            ))}
            {s.live && (
              <Story
                s={s}
                t={{ n: s.live.n, input: s.live.input, prose: s.live.text, status: "streaming", intent: s.live.intent }}
                things={[]} onLook={null}
                live={{ streaming: s.live.streaming, paused: s.live.paused }}
              />
            )}
            {s.pending && !s.live && (
              <article className="ed-turn" data-pending><Headline t={{ input: s.pending }} meta={false} /></article>
            )}
            {waiting(s) && <Weaving label="going to press…" className="ed-weaving" />}
            {/* The room the reply writes into (common.tsx ReadingRoom): nothing on screen moves while it's written. */}
            {s.id && <ReadingRoom s={s} writer=".ed-writer" />}
          </div>

          <Care s={s} className="ed-care" />
          {s.notice && <p className="ed-notice" role="status">{s.notice}</p>}

          {!s.resources && (
            <Writer
              s={s}
              className="ed-writer"
              label={s.id ? "Your next move" : "The first line of your story"}
              placeholder={placeholder}
              sendLabel="File"
            />
          )}

          {starting && home.stories.length > 0 && (
            <section className="ed-shelf" aria-label="Your stories">
              <h2>Your stories</h2>
              <ul>
                {home.stories.map((st) => (
                  <li key={st.id}>
                    <a href={`/s/${st.id}`} onClick={(e) => { e.preventDefault(); home.open(st.id); }}>{st.title}</a>
                    <span>{st.n} {st.n === 1 ? "turn" : "turns"}</span>
                    <DeleteStory title={st.title} remove={() => home.remove(st.id)} />
                  </li>
                ))}
              </ul>
              <p className="ed-kicker"><PlatesLink open={openPlates} label="your plates →" /></p>
            </section>
          )}

          {starting && <footer className="ed-foot ed-home-foot"><TrustLinks meta={meta} /></footer>}
          {starting && <Credit className="ed-credit" />}

          {s.id && (
            <footer className="ed-foot">
              <PlatesLink open={openPlates} />
              <button type="button" onClick={() => meta("stories")}>stories</button>
              <button type="button" onClick={() => meta("map")}>journal</button>
              <button type="button" onClick={() => meta("cloth")}>the cloth</button>
              <button type="button" onClick={() => meta("account")}>account</button>
              <button type="button" onClick={() => meta("feedback")}>feedback</button>
              <More>
                <a href="/privacy" onClick={(e) => { e.preventDefault(); meta("privacy"); }}>privacy</a>
                <a href="/api/export">export</a>
                <button type="button" onClick={() => meta("tour")}>how to play</button>
                <DreamNow s={s} />
                <button type="button" onClick={s.remove}>delete this story</button>
              </More>
            </footer>
          )}
        </div>

        {view && (
          <aside className="ed-side" aria-label="Beside the story">
            <WhereYouAre view={view} onLook={(name) => { const n = s.turns.at(-1)?.n; if (n) s.look(n, name); }} className="ed-where" />
            <Developing s={s} threads={threads} now={now} className="ed-developing-side" />
            {view.recap && (
              <section>
                <h2>Previously</h2>
                <p className="ed-side-recap">{view.recap}</p>
              </section>
            )}
          </aside>
        )}
      </div>
    </main>
  );
}
